import { FunctionComponent as F, Fragment, useState } from "react";
import { useSelector } from 'react-redux';
import { useAppDispatch } from "../../core/store/hooks";
import { IState } from "../../core/store/store";
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CurrencyPoundIcon from '@mui/icons-material/CurrencyPound';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EuroIcon from '@mui/icons-material/Euro';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import { retrieveDosingsAsync, updateFeesAsync } from "../../core/store/protocol/protocol.thunks";
import RetrieveAndLoad from "../UI/Button/RetrieveAndLoad";
import RetrieveSavings from "../UI/Button/RetrieveSavings";
import TextTooltip from "../UI/Tooltip/TextTooltip";
import { updateChecked } from "../../core/store/protocol/protocol.slice";
import NotesIcon from '@mui/icons-material/Notes';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { Box } from "@mui/material";
import { useEditable } from "../../hooks/protocol";


interface Props
{
  protocolId: number,
  headings: { Header: string, accessor: string }[];
}

const ProtocolTableHeader: F<Props> = props =>
{
  const appDispatch = useAppDispatch();
  const user = useSelector((state: IState) => state.user);
  const currencySymbol = useSelector((state: IState) => state.protocol.protocols
    ?.find(p => p.protocolId === Number(props.protocolId))?.currencySymbol);
  const protocol = useSelector((state: IState) => state.protocol.protocols
    ?.find(p => p.protocolId === Number(props.protocolId)));
  const dosings = useSelector((state: IState) => state.protocol.protocols
    ?.find(p => p.protocolId === Number(props.protocolId))?.dosings) || [];
  const editable = useEditable(protocol);

  const [checked, setChecked] = useState(false);

  const handleCheck = () =>
  {
    dosings.forEach(d =>
    {
      const rowChecked = dosings.find(d0 => d0.dosingId === d.dosingId)?.checked;

      if((!checked && !rowChecked) || (checked && rowChecked))
        appDispatch(updateChecked({ protocolId: props.protocolId, dosingId: d.dosingId }));
    });

    setChecked(!checked);
  };

  const handleRetrieveSavings = async() =>
  {
    await appDispatch(retrieveDosingsAsync(props.protocolId, true));
  };

  return (
    <TableHead key='Header'>
      <TableRow key='Header' style={{ alignItems: 'center' }}>
        {props.headings.map((heading, index) =>
        {
          if(!props.headings.filter(h => h.Header).map(h => h.accessor).includes(heading.accessor))
            return <Fragment key={index}/>;

          let textAlign = 'left';
          let displayHeader = <b>{heading.Header}</b>;
          let paddingLeft = '10px';

          switch(heading.accessor)
          {
          case "productName":
            displayHeader = <b style={{ marginRight: 6 }}>{heading.Header + " "}</b>;
            break;
          case "brandName":
            break;
          case "vendorName":
            displayHeader =
              <Stack direction='row' spacing={1} alignItems='center'>
                <b style={{ marginRight: 6 }}>{heading.Header + " "}</b>
                {(user.premium && false) ?
                  <RetrieveAndLoad
                    protocolId={props.protocolId}
                    InitIcon={RetrieveSavings}
                    handleClick={handleRetrieveSavings}
                    loadingTooltip={"Calculating potential savings"}/> :
                  <></>}
              </Stack>;
            break;
          case "dose":
            break;
          case "dosesPerDay":
            textAlign = 'center';
            displayHeader = (
              <TextTooltip lines={[heading.Header]}>
                <MoreTimeIcon/>
              </TextTooltip>);
            break;
          case "daysPerMonth":
            textAlign = 'center';
            displayHeader = (
              <TextTooltip lines={[heading.Header]}>
                <DateRangeIcon/>
              </TextTooltip>);
            break;
          case "productsPerMonth":
            textAlign = 'center';
            displayHeader = (
              <TextTooltip lines={[heading.Header]}>
                <SettingsInputComponentIcon/>
              </TextTooltip>);
            break;
          case "costPerMonth":
            textAlign = 'center';
            displayHeader = (
              <Box sx={{ display:'flex', alignItems: 'center', justifyContent: 'right' }}>
                <TextTooltip lines={[heading.Header + " /" + currencySymbol]}>
                  {(() =>
                  {
                    switch(currencySymbol)
                    {
                    case "£":
                      return <CurrencyPoundIcon/>;
                    case "$":
                      return <AttachMoneyIcon/>;
                    case "€":
                      return <EuroIcon/>;
                    default:
                      return <Typography variant='body2'>{currencySymbol}</Typography>;
                    }
                  })()}
                </TextTooltip>
                <Box sx={{ width: 5 }}/>
                {protocol ?
                  <TextTooltip
                    lines={["Delivery fees and import taxes are " + (protocol.fees ? "ON" : "OFF")]}>
                    <LocalShippingIcon
                      /*@ts-ignore*/
                      fontSize="20px"
                      sx={{
                        color: protocol?.fees ? undefined : 'lightGray',
                        cursor: editable ? 'pointer' : undefined }}
                      onClick={() => editable && appDispatch(updateFeesAsync(protocol.protocolId, !protocol.fees))}/>
                  </TextTooltip> :
                  <></>}
              </Box>);
            break;
          case "note":
            textAlign = 'left';
            paddingLeft = '0px';
            displayHeader = (
              <TextTooltip lines={[heading.Header]}>
                <NotesIcon/>
              </TextTooltip>);
            break;
          case "selection":
          case "doseUnit":
          case "checked":
          case "discounts":
            textAlign = 'center';
            paddingLeft = '0px';
          }


          return (
            <TableCell
              key={index}
              align={textAlign as "inherit" | "left" | "center" | "right" | "justify" | undefined}
              style={{ width: 'fit-content', paddingLeft, paddingRight: "10px" }}>
              {heading.accessor === 'checked' ?
                <Checkbox checked={checked} onChange={handleCheck}/> :
                displayHeader}
            </TableCell>);
        })}
      </TableRow>
    </TableHead>);
};

export default ProtocolTableHeader;
